import { useEffect, useState, useRef, useCallback, memo } from "react";
import { useCurrencies } from "../../../context/CurrenciesContext";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import styles from "./Products.module.scss";
import { ProductsTypes } from "../../../api/types/products.types";
import Loading from "../../../components/common/loading/Loading";
import ProductCard from "../../../components/common/product-card/ProductCard";
import ProductsService from "../../../api/services/products.service";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../context/LanguageContext";
import { groupProductVariants } from "../../../utils/groupProductVariants";
import Button from "../../../components/common/button/Button";

interface FilterData {
  count: number;
  data: ProductsTypes[];
  filters: {
    [key: string]: {
      name: string;
      values: string[];
    };
  };
  price_range: {
    min: number;
    max: number;
  };
}

interface FilterSidebarProps {
  filterData: FilterData | null;
  selectedFilters: { [key: string]: string[] };
  priceRange: [number, number];
  initialPriceRange: [number, number];
  collapsedSections: { [key: string]: boolean };
  currency: string;
  valueTranslationMapRef: React.MutableRefObject<Map<string, string>>;
  reverseTranslationMapRef: React.MutableRefObject<Map<string, string>>;
  t: (key: string, options?: any) => string;
  onFilterChange: (filterName: string, value: string) => void;
  onSliderChange: (value: number | number[]) => void;
  onInputChange: (type: "min" | "max", value: number) => void;
  onClearAllFilters: () => void;
  onToggleSection: (sectionName: string) => void;
}

const FilterSidebar = memo(
  ({
    filterData,
    selectedFilters,
    priceRange,
    initialPriceRange,
    collapsedSections,
    currency,
    valueTranslationMapRef,
    reverseTranslationMapRef,
    t,
    onFilterChange,
    onSliderChange,
    onInputChange,
    onClearAllFilters,
    onToggleSection,
  }: FilterSidebarProps) => {
    if (!filterData) return <div className={styles.filters} />;

    return (
      <div className={styles.filters}>
        <div className={styles.filters_content}>
          <div className={styles.filters_header}>
            <h3>{t("filters")}</h3>
            <button onClick={onClearAllFilters} className={styles.clear_all}>
              {t("clear_all")}
            </button>
          </div>

          <div className={styles.categories_section}>
            <h4 className={styles.section_title}>{t("categories")}</h4>
            <div className={styles.categories_list}>
              {Object.entries(filterData.filters).map(([key, filter]) => (
                <div key={key} className={styles.category_group}>
                  {filter.values.map((value, index) => {
                    const originalValue =
                      valueTranslationMapRef.current.get(value) || value;
                    return (
                      <label key={index} className={styles.filter_option}>
                        <input
                          type="checkbox"
                          checked={
                            selectedFilters[key]?.includes(originalValue) ||
                            false
                          }
                          onChange={() => onFilterChange(key, value)}
                        />
                        <span>{value}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Qiymət aralığı Section */}
          <div className={styles.price_section}>
            <h4 className={styles.section_title}>{t("price_range")}</h4>
            <div className={styles.range_inputs}>
              <div className={styles.input_group}>
                <label>{t("min")}</label>
                <input
                  type="number"
                  value={priceRange[0].toFixed(2)}
                  onChange={(e) => onInputChange("min", Number(e.target.value))}
                  min={filterData.price_range.min}
                  max={filterData.price_range.max}
                />
              </div>
              <span className={styles.separator}>-</span>
              <div className={styles.input_group}>
                <label>{t("max")}</label>
                <input
                  type="number"
                  value={priceRange[1].toFixed(2)}
                  onChange={(e) => onInputChange("max", Number(e.target.value))}
                  min={filterData.price_range.min}
                  max={filterData.price_range.max}
                />
              </div>
            </div>
            <div className={styles.slider_container}>
              <Slider
                range
                min={filterData.price_range.min}
                max={filterData.price_range.max}
                value={priceRange}
                onChange={onSliderChange}
                allowCross={false}
                className={styles.slider}
              />
            </div>
          </div>

          {/* Təsdiqlə Button */}
          <button className={styles.confirm_button}>{t("confirm")}</button>

          {/* Mağazalar Section */}
          <div className={styles.collapsible_section}>
            <div
              className={styles.section_header}
              onClick={() => onToggleSection("stores")}
            >
              <h4 className={styles.section_title}>{t("stores")}</h4>
              <svg
                className={`${styles.arrow} ${
                  collapsedSections["stores"] ? styles.collapsed : ""
                }`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {!collapsedSections["stores"] && (
              <div className={styles.categories_list}>
                {Object.entries(filterData.filters)
                  .filter(
                    ([key]) =>
                      key.toLowerCase().includes("store") ||
                      key.toLowerCase().includes("mağaza"),
                  )
                  .map(([key, filter]) => (
                    <div key={key} className={styles.category_group}>
                      {filter.values.map((value, index) => {
                        const originalValue =
                          valueTranslationMapRef.current.get(value) || value;
                        return (
                          <label key={index} className={styles.filter_option}>
                            <input
                              type="checkbox"
                              checked={
                                selectedFilters[key]?.includes(originalValue) ||
                                false
                              }
                              onChange={() => onFilterChange(key, value)}
                            />
                            <span>{value}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Rəng Section */}
          <div className={styles.collapsible_section}>
            <div
              className={styles.section_header}
              onClick={() => onToggleSection("color")}
            >
              <h4 className={styles.section_title}>{t("color")}</h4>
              <svg
                className={`${styles.arrow} ${
                  collapsedSections["color"] ? styles.collapsed : ""
                }`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {!collapsedSections["color"] && (
              <div className={styles.color_list}>
                {Object.entries(filterData.filters)
                  .filter(
                    ([key]) =>
                      key.toLowerCase().includes("color") ||
                      key.toLowerCase().includes("rəng") ||
                      key.toLowerCase().includes("rang"),
                  )
                  .map(([key, filter]) => (
                    <div key={key} className={styles.color_group}>
                      {filter.values.map((value, index) => {
                        const originalValue =
                          valueTranslationMapRef.current.get(value) || value;
                        const colorMap: { [key: string]: string } = {
                          ağ: "#FFFFFF",
                          white: "#FFFFFF",
                          qara: "#000000",
                          black: "#000000",
                          yaşıl: "#22C55E",
                          green: "#22C55E",
                          göy: "#3B82F6",
                          blue: "#3B82F6",
                          qırmızı: "#EF4444",
                          red: "#EF4444",
                          sarı: "#EAB308",
                          yellow: "#EAB308",
                          bənövşəyi: "#8B5CF6",
                          purple: "#8B5CF6",
                        };
                        const colorValue =
                          colorMap[value.toLowerCase()] || "#9CA3AF";
                        return (
                          <label key={index} className={styles.color_option}>
                            <input
                              type="radio"
                              name={`color-${key}`}
                              checked={
                                selectedFilters[key]?.includes(originalValue) ||
                                false
                              }
                              onChange={() => onFilterChange(key, value)}
                            />
                            <div
                              className={styles.color_circle}
                              style={{
                                backgroundColor: colorValue,
                                border:
                                  colorValue === "#FFFFFF"
                                    ? "2px solid #e5e7eb"
                                    : "2px solid transparent",
                              }}
                            ></div>
                            <span>{value}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Ölçü Section */}
          <div className={styles.collapsible_section}>
            <div
              className={styles.section_header}
              onClick={() => onToggleSection("size")}
            >
              <h4 className={styles.section_title}>{t("size")}</h4>
              <svg
                className={`${styles.arrow} ${
                  collapsedSections["size"] ? styles.collapsed : ""
                }`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {!collapsedSections["size"] && (
              <div className={styles.categories_list}>
                {Object.entries(filterData.filters)
                  .filter(
                    ([key]) =>
                      key.toLowerCase().includes("size") ||
                      key.toLowerCase().includes("ölçü") ||
                      key.toLowerCase().includes("olcu"),
                  )
                  .map(([key, filter]) => (
                    <div key={key} className={styles.category_group}>
                      {filter.values.map((value, index) => {
                        const originalValue =
                          valueTranslationMapRef.current.get(value) || value;
                        return (
                          <label key={index} className={styles.filter_option}>
                            <input
                              type="checkbox"
                              checked={
                                selectedFilters[key]?.includes(originalValue) ||
                                false
                              }
                              onChange={() => onFilterChange(key, value)}
                            />
                            <span>{value}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Ölçüyə görə Section */}
          <div className={styles.size_grid_section}>
            <div
              className={styles.section_header}
              onClick={() => onToggleSection("sizeGrid")}
            >
              <h4 className={styles.section_title}>{t("size_by")}</h4>
              <svg
                className={`${styles.arrow} ${
                  collapsedSections["sizeGrid"] ? styles.collapsed : ""
                }`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {!collapsedSections["sizeGrid"] && (
              <div className={styles.size_grid}>
                {Object.entries(filterData.filters)
                  .filter(
                    ([key]) =>
                      key.toLowerCase().includes("size") ||
                      key.toLowerCase().includes("ölçü") ||
                      key.toLowerCase().includes("olcu"),
                  )
                  .flatMap(([, filter]) => filter.values)
                  .map((size, index) => (
                    <button key={index} className={styles.size_box}>
                      {size}
                    </button>
                  ))}
              </div>
            )}
            {!collapsedSections["sizeGrid"] && (
              <button className={styles.show_more}>{t("show_all")}</button>
            )}
          </div>

          {/* Active Filters Section */}
          {(Object.keys(selectedFilters).length > 0 ||
            priceRange[0] !== initialPriceRange[0] ||
            priceRange[1] !== initialPriceRange[1]) && (
            <div className={styles.active_filters_section}>
              {Object.entries(selectedFilters).map(([filterKey, values]) => {
                const filterInfo = filterData.filters[filterKey];
                if (!filterInfo || values.length === 0) return null;
                return (
                  <div key={filterKey} className={styles.active_filter_item}>
                    <span className={styles.filter_label}>
                      {filterInfo.name}:
                    </span>
                    <div className={styles.filter_values}>
                      {values.map((value, idx) => {
                        const displayValue =
                          reverseTranslationMapRef.current.get(value) || value;
                        return (
                          <span key={idx} className={styles.filter_value}>
                            {displayValue}
                            {idx < values.length - 1 && ", "}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {(priceRange[0] !== initialPriceRange[0] ||
                priceRange[1] !== initialPriceRange[1]) && (
                <div className={styles.active_filter_item}>
                  <span className={styles.filter_label}>{t("price")}:</span>
                  <div className={styles.filter_values}>
                    <span className={styles.filter_value}>
                      {priceRange[0].toFixed(2)} {currency} -{" "}
                      {priceRange[1].toFixed(2)} {currency}
                    </span>
                  </div>
                </div>
              )}

              <button
                className={styles.clear_all_button}
                onClick={onClearAllFilters}
              >
                {t("clear_all")}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

FilterSidebar.displayName = "FilterSidebar";

const Products = () => {
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: string[];
  }>({});
  const [hasInitialProducts, setHasInitialProducts] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
  const [initialPriceRange, setInitialPriceRange] = useState<[number, number]>([
    0, 0,
  ]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(true);
  const PRODUCTS_PER_PAGE = 20;
  const INFINITE_SCROLL_LIMIT = 300;
  const { id } = useParams();
  const { t } = useTranslation("translation");
  const priceRangeChangedRef = useRef(false);
  const filtersChangedRef = useRef(false);
  const { currentLanguage } = useLanguage();
  const { currency, currencyId } = useCurrencies();
  const variantsRef = useRef<ProductsTypes[]>([]);
  const observerTarget = useRef<HTMLDivElement>(null);
  const pageRef = useRef(1);
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const [discountedProducts, setDiscountedProducts] = useState<ProductsTypes[]>(
    [],
  );
  const [discountedLoading, setDiscountedLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  const valueTranslationMapRef = useRef<Map<string, string>>(new Map());
  const reverseTranslationMapRef = useRef<Map<string, string>>(new Map());

  const toggleSection = useCallback((sectionName: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  }, []);

  const getTranslation = (
    obj: any,
    prefix: string,
    lang: string,
    fallbackValue?: string,
  ) => {
    const keys = [`${prefix}_${lang}`, `${prefix}_en`, prefix];

    for (const key of keys) {
      if (obj[key]) return obj[key];
    }

    return fallbackValue;
  };

  const getLocalizedAttributeName = useCallback(
    (attribute: ProductsTypes["attributes"][number], language: string) => {
      return getTranslation(attribute, "attribute_name", language);
    },
    [],
  );

  const getLocalizedAttributeValue = useCallback(
    (
      attribute: ProductsTypes["attributes"][number],
      language: string,
      fallback: string,
    ) => {
      return getTranslation(attribute, "value", language, fallback);
    },
    [],
  );

  const buildLocalizedFilters = useCallback(
    (
      filters: FilterData["filters"],
      variants: ProductsTypes[],
      language: string,
    ): FilterData["filters"] => {
      if (!filters || !variants || variants.length === 0) return filters;

      const nameLookup = new Map<string, ProductsTypes["attributes"][number]>();
      const valueLookup = new Map<
        string,
        ProductsTypes["attributes"][number]
      >();

      variants.forEach((variant) => {
        variant.attributes?.forEach((attr) => {
          [
            attr.attribute_name,
            attr.attribute_name_az,
            attr.attribute_name_en,
            attr.attribute_name_es,
            attr.attribute_name_de,
          ]
            .filter(Boolean)
            .forEach((n) => nameLookup.set(n!, attr));

          [
            attr.value,
            attr.value_az,
            attr.value_en,
            attr.value_es,
            attr.value_de,
          ]
            .filter(Boolean)
            .forEach((v) => valueLookup.set(v!, attr));
        });
      });

      const localizedFilters: FilterData["filters"] = {};

      Object.entries(filters).forEach(([key, filter]) => {
        const attributeForName = nameLookup.get(filter.name);
        const displayName = attributeForName
          ? getLocalizedAttributeName(attributeForName, language)
          : filter.name;

        const localizedValues = filter.values.map((val) => {
          const attributeForValue = valueLookup.get(val);
          const localizedValue = attributeForValue
            ? getLocalizedAttributeValue(attributeForValue, language, val)
            : val;

          if (localizedValue !== val) {
            valueTranslationMapRef.current.set(localizedValue, val);
            reverseTranslationMapRef.current.set(val, localizedValue);
          }

          return localizedValue;
        });

        localizedFilters[key] = {
          name: displayName,
          values: Array.from(new Set(localizedValues)),
        };
      });

      return localizedFilters;
    },
    [getLocalizedAttributeName, getLocalizedAttributeValue],
  );

  const applyFilters = useCallback(
    async (pageNum: number = 1, isInitial: boolean = false) => {
      try {
        if (currencyId === undefined || currencyId === null) {
          return;
        }

        isInitial ? setFilterLoading(true) : setLoadingMore(true);

        const queryParams = new URLSearchParams();
        queryParams.append("page", pageNum.toString());
        queryParams.append("count", PRODUCTS_PER_PAGE.toString());

        if (
          priceRange[0] !== initialPriceRange[0] ||
          priceRange[1] !== initialPriceRange[1]
        ) {
          queryParams.append("min_price", priceRange[0].toString());
          queryParams.append("max_price", priceRange[1].toString());
        }
        Object.entries(selectedFilters).forEach(([filterName, values]) => {
          if (values && values.length > 0) {
            const cleanFilterName = filterName.trim();
            queryParams.append(cleanFilterName, values.join(","));
          }
        });

        const res = await ProductsService.filterProducts(
          Number(id),
          Number(currencyId),
          queryParams,
        );
        const variants = res.data.data as ProductsTypes[];
        variantsRef.current = isInitial
          ? variants
          : [...variantsRef.current, ...variants];
        const groupedProducts = groupProductVariants(variants);
        const count = res.data.count || 0;

        if (isInitial && res.data.price_range) {
          const localizedFilters = buildLocalizedFilters(
            res.data.filters,
            variants,
            currentLanguage,
          );
          setFilterData((prev) => {
            if (prev) {
              return {
                ...prev,
                price_range: res.data.price_range,
                filters: localizedFilters,
                count: count,
              };
            }
            return { ...res.data, filters: localizedFilters };
          });
        }

        const isPriceRangeChanged =
          priceRange[0] !== initialPriceRange[0] ||
          priceRange[1] !== initialPriceRange[1];

        const filteredByPrice = isPriceRangeChanged
          ? groupedProducts.filter((product) => {
              const rawPrice =
                (product.discount_price as number | undefined) ??
                (product.price as number | undefined);

              if (rawPrice == null) {
                return true;
              }

              const value = Number(rawPrice);
              if (Number.isNaN(value)) {
                return true;
              }

              return value >= priceRange[0] && value <= priceRange[1];
            })
          : groupedProducts;

        // Append products for infinite scroll, replace for initial load or filter changes
        setProducts((prev) => {
          const updatedProducts = isInitial
            ? filteredByPrice
            : [...prev, ...filteredByPrice];

          // Calculate hasMore based on the actual number of products after frontend filtering
          const totalFilteredLoaded = updatedProducts.length;
          const noNewProducts = filteredByPrice.length === 0;
          // Use backend count for comparison, but also check if we got fewer items than expected
          const backendItemsLoaded = pageNum * PRODUCTS_PER_PAGE;
          setHasMore(
            !noNewProducts &&
              backendItemsLoaded < count &&
              totalFilteredLoaded < count,
          );

          return updatedProducts;
        });

        if (isInitial) {
          setPage(pageNum);
          pageRef.current = pageNum;
          setHasInitialProducts(filteredByPrice.length > 0);
        } else {
          pageRef.current = pageNum;
        }

        priceRangeChangedRef.current = false;
        filtersChangedRef.current = false;
      } catch (error) {
        console.error("Error applying filters:", error);
      } finally {
        setLoading(false);
        setFilterLoading(false);
        setLoadingMore(false);
      }
    },
    [
      id,
      currencyId,
      priceRange,
      initialPriceRange,
      selectedFilters,
      currentLanguage,
      buildLocalizedFilters,
    ],
  );

  useEffect(() => {
    if (currencyId === undefined || currencyId === null) {
      return;
    }

    setLoading(true);
    setPage(1); // Reset to first page when category or currency changes
    pageRef.current = 1;
    // Reset price range when currency changes to avoid using old currency values
    setPriceRange([0, 0]);
    setInitialPriceRange([0, 0]);
    const getData = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", "1");
        queryParams.append("count", PRODUCTS_PER_PAGE.toString());

        const res = await ProductsService.filterProducts(
          Number(id),
          Number(currencyId),
          queryParams,
        );
        console.log(res.data);
        const variants = res.data.data as ProductsTypes[];
        variantsRef.current = variants;

        const groupedProducts = groupProductVariants(variants);
        const count = res.data.count || 0;
        setHasInitialProducts(groupedProducts.length > 0);
        setProducts(groupedProducts);
        setHasMore(count > PRODUCTS_PER_PAGE);
        setUseInfiniteScroll(true);
        const localizedFilters = buildLocalizedFilters(
          res.data.filters,
          variants,
          currentLanguage,
        );
        setFilterData({ ...res.data, filters: localizedFilters });
        if (res.data.price_range) {
          const min = res.data.price_range.min;
          const max = res.data.price_range.max;
          setPriceRange([min, max]);
          setInitialPriceRange([min, max]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [id, currencyId, buildLocalizedFilters, currentLanguage]);

  useEffect(() => {
    if (!variantsRef.current.length) {
      return;
    }

    setFilterData((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedFilters = buildLocalizedFilters(
        prev.filters,
        variantsRef.current,
        currentLanguage,
      );

      return { ...prev, filters: updatedFilters };
    });
  }, [currentLanguage, buildLocalizedFilters]);

  useEffect(() => {
    if (currencyId === undefined || currencyId === null) {
      return;
    }

    const fetchDiscountedProducts = async () => {
      try {
        setDiscountedLoading(true);
        const res = await ProductsService.getAllProducts(1, 10, currencyId);
        const allProducts = res.data.data as ProductsTypes[];

        // Filter products that have discounts
        const discounted = allProducts.filter(
          (product) =>
            product.discount_price &&
            product.discount_price > 0 &&
            product.discount_percent &&
            product.discount_percent > 0,
        );

        const groupedDiscounted = groupProductVariants(discounted);
        setDiscountedProducts(groupedDiscounted);
      } catch (error) {
        console.error("Error fetching discounted products:", error);
      } finally {
        setDiscountedLoading(false);
      }
    };

    fetchDiscountedProducts();
  }, [currencyId]);

  useEffect(() => {
    if (filterData && filtersChangedRef.current) {
      filtersChangedRef.current = false;
      setPage(1);
      pageRef.current = 1;
      setUseInfiniteScroll(true);
      const timer = setTimeout(() => {
        applyFilters(1, true);
      }, 300);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, applyFilters]);

  useEffect(() => {
    // Only trigger when priceRange actually changes and it's a user-initiated change
    // filterData changes should not trigger a new API call
    if (filterData && priceRangeChangedRef.current) {
      priceRangeChangedRef.current = false; // Reset flag
      setPage(1); // Reset to first page when price range changes
      pageRef.current = 1;
      setUseInfiniteScroll(true); // Reset infinite scroll
      const timer = setTimeout(() => {
        applyFilters(1, true); // Reset to page 1, initial load
      }, 500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceRange, applyFilters]);

  useEffect(() => {
    if (products.length >= INFINITE_SCROLL_LIMIT && useInfiniteScroll) {
      setUseInfiniteScroll(false);
    } else if (products.length < INFINITE_SCROLL_LIMIT && !useInfiniteScroll) {
      setUseInfiniteScroll(true);
    }
  }, [products.length, useInfiniteScroll]);

  useEffect(() => {
    if (!useInfiniteScroll || !hasMore || loadingMore || loading || !filterData)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = pageRef.current + 1;
          pageRef.current = nextPage;
          setPage(nextPage);
          applyFilters(nextPage, false);
        }
      },
      { threshold: 0.5 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);

    return () => observer.disconnect();
    // filterData is needed here to set up observer when data is first loaded
    // This doesn't cause a loop because the observer doesn't trigger applyFilters directly
  }, [
    useInfiniteScroll,
    hasMore,
    loadingMore,
    loading,
    filterData,
    applyFilters,
  ]);

  const handleSliderChange = useCallback((value: number | number[]) => {
    if (Array.isArray(value)) {
      priceRangeChangedRef.current = true;
      setPriceRange([value[0], value[1]]);
    }
  }, []);

  const handleInputChange = useCallback(
    (type: "min" | "max", value: number) => {
      priceRangeChangedRef.current = true;
      if (type === "min") {
        setPriceRange((prev) => [Math.min(value, prev[1]), prev[1]]);
      } else {
        setPriceRange((prev) => [prev[0], Math.max(value, prev[0])]);
      }
    },
    [],
  );

  const handleFilterChange = useCallback(
    (filterName: string, value: string) => {
      filtersChangedRef.current = true;

      // Get original value from translation map if it exists
      const originalValue = valueTranslationMapRef.current.get(value) || value;

      setSelectedFilters((prev: any) => {
        const currentFilters = prev[filterName] || [];
        const isSelected = currentFilters.includes(originalValue);

        if (isSelected) {
          const updatedFilters = currentFilters.filter(
            (v: any) => v !== originalValue,
          );
          const newFilters = {
            ...prev,
            [filterName]:
              updatedFilters.length > 0 ? updatedFilters : undefined,
          };
          Object.keys(newFilters).forEach((key) => {
            if (!newFilters[key] || newFilters[key].length === 0) {
              delete newFilters[key];
            }
          });

          return newFilters;
        } else {
          return {
            ...prev,
            [filterName]: [...currentFilters, originalValue],
          };
        }
      });
    },
    [],
  );

  const clearAllFilters = useCallback(() => {
    setSelectedFilters({});
    setPage(1);
    setUseInfiniteScroll(true);
    if (filterData?.price_range) {
      setPriceRange([filterData.price_range.min, filterData.price_range.max]);
      setInitialPriceRange([
        filterData.price_range.min,
        filterData.price_range.max,
      ]);
    }
    priceRangeChangedRef.current = true;
    filtersChangedRef.current = true;
  }, [filterData]);

  const removeFilter = useCallback((filterName: string, value: string) => {
    filtersChangedRef.current = true;
    setSelectedFilters((prev) => {
      const currentFilters = prev[filterName] || [];
      const updatedFilters = currentFilters.filter((v) => v !== value);

      if (updatedFilters.length === 0) {
        const newFilters = { ...prev };
        delete newFilters[filterName];
        return newFilters;
      }

      return {
        ...prev,
        [filterName]: updatedFilters,
      };
    });
  }, []);

  const removePriceFilter = useCallback(() => {
    if (filterData?.price_range) {
      setPriceRange([filterData.price_range.min, filterData.price_range.max]);
      setInitialPriceRange([
        filterData.price_range.min,
        filterData.price_range.max,
      ]);
      priceRangeChangedRef.current = true;
    }
  }, [filterData]);

  const goToPage = (pageNum: number) => {
    setPage(pageNum);
    pageRef.current = pageNum;
    applyFilters(pageNum, false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getLocalizedName = (product: ProductsTypes) => {
    switch (currentLanguage) {
      case "az":
        return product.name_az || product.name_en;
      case "de":
        return product.name_de || product.name_en;
      case "es":
        return product.name_es || product.name_en;
      case "en":
      default:
        return product.name_en;
    }
  };

  const getEmptyStateMessage = () => {
    const isPriceRangeChanged =
      priceRange[0] !== initialPriceRange[0] ||
      priceRange[1] !== initialPriceRange[1];

    const hasSelectedFilters = Object.keys(selectedFilters).length > 0;
    const isFiltering = isPriceRangeChanged || hasSelectedFilters;

    if (hasInitialProducts && isFiltering) {
      return t("no_products_match_filters", {
        defaultValue: "Seçilmiş filtrlərə uyğun məhsul tapılmadı.",
      });
    }

    return t("no_products_found");
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.products}>
      <TitleUpdater title={t("products")} />
      <div className="container">
        <div className={styles.product_container}>
          <FilterSidebar
            filterData={filterData}
            selectedFilters={selectedFilters}
            priceRange={priceRange}
            initialPriceRange={initialPriceRange}
            collapsedSections={collapsedSections}
            currency={currency}
            valueTranslationMapRef={valueTranslationMapRef}
            reverseTranslationMapRef={reverseTranslationMapRef}
            t={t}
            onFilterChange={handleFilterChange}
            onSliderChange={handleSliderChange}
            onInputChange={handleInputChange}
            onClearAllFilters={clearAllFilters}
            onToggleSection={toggleSection}
          />
          <div className={styles.products_section}>
            {/* Products Header - Count and Active Filters */}
            {filterData && (
              <div className={styles.products_header}>
                <div className={styles.products_count}>
                  {t("total_products", { defaultValue: "Toplam məhsul" })}:{" "}
                  <strong>{filterData.count}</strong>
                </div>

                {/* Active Filters Pills */}
                {(Object.keys(selectedFilters).length > 0 ||
                  priceRange[0] !== initialPriceRange[0] ||
                  priceRange[1] !== initialPriceRange[1]) && (
                  <div className={styles.active_filters_pills}>
                    <div className={styles.pills_container}>
                      {/* Selected Filters Pills */}
                      {Object.entries(selectedFilters).map(
                        ([filterKey, values]) => {
                          const filterInfo = filterData.filters[filterKey];
                          if (!filterInfo || values.length === 0) return null;

                          return values.map((value, idx) => {
                            // Get localized value for display
                            const displayValue =
                              reverseTranslationMapRef.current.get(value) ||
                              value;

                            return (
                              <div
                                key={`${filterKey}-${idx}`}
                                className={styles.filter_pill}
                              >
                                <span>
                                  {filterInfo.name}: {displayValue}
                                </span>
                                <button
                                  onClick={() => removeFilter(filterKey, value)}
                                  className={styles.pill_remove}
                                  aria-label="Remove filter"
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          });
                        },
                      )}

                      {/* Price Range Pill */}
                      {(priceRange[0] !== initialPriceRange[0] ||
                        priceRange[1] !== initialPriceRange[1]) && (
                        <div className={styles.filter_pill}>
                          <span>
                            {t("price_range", { defaultValue: "Qiymət" })}:{" "}
                            {priceRange[0].toFixed(2)} {currency} -{" "}
                            {priceRange[1].toFixed(2)} {currency}
                          </span>
                          <button
                            onClick={removePriceFilter}
                            className={styles.pill_remove}
                            aria-label="Remove price filter"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={clearAllFilters}
                      className={styles.clear_filters_link}
                    >
                      {t("clear_all", { defaultValue: "Hamısını sil" })}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Products Grid */}
            <div className={styles.products_list}>
              {filterLoading ? (
                <div className={styles.scroll_trigger}>
                  <Loading />
                </div>
              ) : products.length > 0 ? (
                <>
                  {products.map((p, i) => (
                    <ProductCard
                      id={p.id}
                      key={i}
                      title={getLocalizedName(p)}
                      currency={p.currency_name}
                      price={p.price?.toString()}
                      discount_percent={p.discount_percent?.toString()}
                      discount_price={p.discount_price?.toString()}
                      image={p.images ? p.images[0]?.image : ""}
                      stock_quantity={p.stock_quantity}
                      wishlist={p.is_wish}
                      images={p.images}
                      attributes={p.attributes}
                    />
                  ))}
                  {useInfiniteScroll && hasMore && (
                    <div ref={observerTarget} className={styles.scroll_trigger}>
                      {loadingMore && <Loading />}
                    </div>
                  )}
                  {!useInfiniteScroll && hasMore && (
                    <div className={styles.load_more_container}>
                      <Button
                        onClick={() => {
                          const nextPage = page + 1;
                          setPage(nextPage);
                          applyFilters(nextPage, false);
                        }}
                        loading={loadingMore}
                        disabled={loadingMore}
                        className={styles.load_more_btn}
                      >
                        {t("load_more", { defaultValue: "Daha çox" })}
                      </Button>
                    </div>
                  )}

                  {/* Pagination */}
                  {!useInfiniteScroll && filterData && (
                    <div className={styles.pagination_container}>
                      <div className={styles.pagination}>
                        {/* Previous Button */}
                        <button
                          className={styles.pagination_arrow}
                          onClick={() => goToPage(page - 1)}
                          disabled={page === 1}
                        >
                          ←
                        </button>

                        {/* Page Numbers */}
                        {(() => {
                          const totalPages = Math.ceil(
                            filterData.count / PRODUCTS_PER_PAGE,
                          );
                          const pageNumbers = [];
                          const maxVisiblePages = 5;

                          let startPage = Math.max(
                            1,
                            page - Math.floor(maxVisiblePages / 2),
                          );
                          let endPage = Math.min(
                            totalPages,
                            startPage + maxVisiblePages - 1,
                          );

                          if (endPage - startPage < maxVisiblePages - 1) {
                            startPage = Math.max(
                              1,
                              endPage - maxVisiblePages + 1,
                            );
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pageNumbers.push(
                              <button
                                key={i}
                                className={`${styles.pagination_number} ${
                                  i === page ? styles.active : ""
                                }`}
                                onClick={() => goToPage(i)}
                              >
                                {i}
                              </button>,
                            );
                          }

                          return pageNumbers;
                        })()}

                        {/* Next Button */}
                        <button
                          className={styles.pagination_arrow}
                          onClick={() => goToPage(page + 1)}
                          disabled={
                            page >=
                            Math.ceil(filterData.count / PRODUCTS_PER_PAGE)
                          }
                        >
                          →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.no_products}>
                  {getEmptyStateMessage()}
                </div>
              )}
            </div>
          </div>
        </div>

        {discountedProducts.length > 0 && (
          <div className={styles.discounted_section}>
            <div className={styles.discounted_header}>
              <h2>
                {t("discounted_products", {
                  defaultValue: "Endirimli məhsullar",
                })}
              </h2>
              <button className={styles.view_all}>
                {t("view_all", { defaultValue: "Hamısına bax" })}
              </button>
            </div>

            {discountedLoading ? (
              <div className={styles.discounted_loading}>
                <Loading />
              </div>
            ) : (
              <div className={styles.discounted_grid}>
                {discountedProducts.map((product, index) => (
                  <div key={index} className={styles.discounted_card}>
                    <ProductCard
                      id={product.id}
                      title={getLocalizedName(product)}
                      currency={product.currency_name}
                      price={product.price?.toString()}
                      discount_percent={product.discount_percent?.toString()}
                      discount_price={product.discount_price?.toString()}
                      image={product.images ? product.images[0]?.image : ""}
                      stock_quantity={product.stock_quantity}
                      wishlist={product.is_wish}
                      images={product.images}
                      attributes={product.attributes}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
